import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { calibrationApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Gauge, Plus, CheckCircle, XCircle, FileCheck, Bluetooth, BluetoothConnected, BluetoothOff, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, getTodayDate, cn } from '../lib/utils';
import { useInkbirdThermometer } from '../hooks/useInkbirdThermometer';

function isIcePointOk(val) {
    const v = parseFloat(val);
    return !isNaN(v) && v >= -1 && v <= 1;
}
function isBoilingPointOk(val) {
    const v = parseFloat(val);
    return !isNaN(v) && v >= 99 && v <= 101;
}

export default function CalibrationLogs() {
    const { isManagerOrAdmin: isManager } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        probe_name: '', calibration_date: getTodayDate(),
        ice_point_reading: '', boiling_point_reading: '',
        passed: true, corrective_action: '', next_due_date: '', notes: ''
    });
    const thermometer = useInkbirdThermometer();

    const loadLogs = () => {
        setLoading(true);
        calibrationApi.getAll().then(({ data }) => setLogs(data)).catch(() => toast.error('Failed to load calibration logs')).finally(() => setLoading(false));
    };

    useEffect(() => { loadLogs(); }, []);

    const handleCreate = async () => {
        if (!form.probe_name || form.ice_point_reading === '' || form.boiling_point_reading === '') {
            toast.error('Probe name and readings are required'); return;
        }
        const iceOk = isIcePointOk(form.ice_point_reading);
        const boilOk = isBoilingPointOk(form.boiling_point_reading);
        const passed = iceOk && boilOk;
        try {
            await calibrationApi.create({
                probe_name: form.probe_name,
                calibration_date: form.calibration_date,
                ice_point_reading: parseFloat(form.ice_point_reading),
                boiling_point_reading: parseFloat(form.boiling_point_reading),
                passed,
                corrective_action: !passed ? form.corrective_action : null,
                next_due_date: form.next_due_date || null,
                notes: form.notes || null,
            });
            toast.success('Calibration log recorded');
            setShowForm(false);
            setForm({ probe_name: '', calibration_date: getTodayDate(), ice_point_reading: '', boiling_point_reading: '', passed: true, corrective_action: '', next_due_date: '', notes: '' });
            loadLogs();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        }
    };

    const handleSignOff = async (logId) => {
        try {
            await calibrationApi.signOff(logId);
            toast.success('Signed off');
            loadLogs();
        } catch { toast.error('Failed to sign off'); }
    };

    return (
        <Layout>
            <div data-testid="calibration-logs-page">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
                            <Gauge className="h-6 w-6 brand-icon" /> Probe Calibration Logs
                        </h1>
                        <p className="text-slate-400 text-sm mt-1">Record and track probe thermometer calibrations</p>
                    </div>
                    <Button onClick={() => setShowForm(!showForm)} className="brand-btn font-medium" data-testid="add-calibration-button">
                        <Plus className="h-4 w-4 mr-1" /> New Calibration
                    </Button>
                </div>

                {showForm && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mb-6" data-testid="calibration-form">
                        <h2 className="text-lg font-semibold text-slate-200 mb-4">Record Calibration</h2>

                        {/* Thermometer status bar */}
                        {thermometer.bleSupported && (
                            <div className={cn(
                                "flex items-center gap-3 p-2.5 rounded-lg border text-sm mb-4",
                                thermometer.connected ? "bg-emerald-500/10 border-emerald-500/30" : "bg-slate-800/50 border-slate-700"
                            )} data-testid="calibration-thermometer-status">
                                {thermometer.connected ? (
                                    <>
                                        <BluetoothConnected className="h-4 w-4 text-emerald-400 shrink-0" />
                                        <span className="text-emerald-400 font-medium flex-1 truncate">
                                            {thermometer.deviceName} — {thermometer.temperature !== null ? `${thermometer.temperature.toFixed(1)}°C` : 'Waiting...'}
                                        </span>
                                        <Button size="sm" variant="outline" onClick={thermometer.disconnect}
                                            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs h-7 shrink-0" data-testid="calibration-disconnect-thermometer">
                                            <BluetoothOff className="h-3 w-3 mr-1" /> Disconnect
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Bluetooth className="h-4 w-4 text-slate-400 shrink-0" />
                                        <span className="text-slate-400 flex-1">Connect thermometer for ice/boiling point readings</span>
                                        <Button size="sm" onClick={async () => {
                                            const r = await thermometer.connect();
                                            if (r.success) toast.success(`Connected to ${r.deviceName}`);
                                            else if (r.error !== 'Pairing cancelled') toast.error(r.error);
                                        }} disabled={thermometer.connecting}
                                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 shrink-0" data-testid="calibration-connect-thermometer">
                                            {thermometer.connecting ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Connecting...</> : <><Bluetooth className="h-3 w-3 mr-1" /> Connect</>}
                                        </Button>
                                    </>
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-slate-300">Probe Name/ID *</Label>
                                <Input value={form.probe_name} onChange={(e) => setForm({ ...form, probe_name: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="e.g. Probe 1, Kitchen Probe" data-testid="calibration-probe-name" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Calibration Date</Label>
                                <Input type="date" value={form.calibration_date} onChange={(e) => setForm({ ...form, calibration_date: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="calibration-date" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Ice Point Reading (°C) *</Label>
                                <div className="flex gap-1.5 mt-1">
                                    <Input type="number" step="0.1" value={form.ice_point_reading}
                                        onChange={(e) => setForm({ ...form, ice_point_reading: e.target.value })}
                                        className={cn("bg-slate-800 border-slate-700 text-slate-100 flex-1",
                                            form.ice_point_reading !== '' && !isIcePointOk(form.ice_point_reading) && "border-rose-500 focus:border-rose-500"
                                        )}
                                        placeholder="Expected: 0°C" data-testid="calibration-ice-point" />
                                    {thermometer.connected && thermometer.temperature !== null && (
                                        <Button type="button" size="sm" variant="outline"
                                            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 h-10 px-3 text-xs shrink-0"
                                            onClick={() => setForm({ ...form, ice_point_reading: thermometer.temperature.toFixed(1) })}
                                            data-testid="calibration-use-reading-ice">
                                            Use Reading
                                        </Button>
                                    )}
                                </div>
                                {form.ice_point_reading !== '' ? (
                                    isIcePointOk(form.ice_point_reading) ? (
                                        <p className="text-[11px] text-emerald-400 mt-1 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Within tolerance (-1 to 1°C)</p>
                                    ) : (
                                        <p className="text-[11px] text-rose-400 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Outside tolerance! Expected -1 to 1°C</p>
                                    )
                                ) : (
                                    <p className="text-[11px] text-slate-500 mt-1">Acceptable range: -1°C to +1°C</p>
                                )}
                            </div>
                            <div>
                                <Label className="text-slate-300">Boiling Point Reading (°C) *</Label>
                                <div className="flex gap-1.5 mt-1">
                                    <Input type="number" step="0.1" value={form.boiling_point_reading}
                                        onChange={(e) => setForm({ ...form, boiling_point_reading: e.target.value })}
                                        className={cn("bg-slate-800 border-slate-700 text-slate-100 flex-1",
                                            form.boiling_point_reading !== '' && !isBoilingPointOk(form.boiling_point_reading) && "border-rose-500 focus:border-rose-500"
                                        )}
                                        placeholder="Expected: 100°C" data-testid="calibration-boiling-point" />
                                    {thermometer.connected && thermometer.temperature !== null && (
                                        <Button type="button" size="sm" variant="outline"
                                            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 h-10 px-3 text-xs shrink-0"
                                            onClick={() => setForm({ ...form, boiling_point_reading: thermometer.temperature.toFixed(1) })}
                                            data-testid="calibration-use-reading-boiling">
                                            Use Reading
                                        </Button>
                                    )}
                                </div>
                                {form.boiling_point_reading !== '' ? (
                                    isBoilingPointOk(form.boiling_point_reading) ? (
                                        <p className="text-[11px] text-emerald-400 mt-1 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Within tolerance (99 to 101°C)</p>
                                    ) : (
                                        <p className="text-[11px] text-rose-400 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Outside tolerance! Expected 99 to 101°C</p>
                                    )
                                ) : (
                                    <p className="text-[11px] text-slate-500 mt-1">Acceptable range: 99°C to 101°C</p>
                                )}
                            </div>

                            {/* Auto-computed pass/fail indicator */}
                            {form.ice_point_reading !== '' && form.boiling_point_reading !== '' && (
                                <div className="sm:col-span-2">
                                    <div className={cn("flex items-center gap-2 p-3 rounded-lg border",
                                        isIcePointOk(form.ice_point_reading) && isBoilingPointOk(form.boiling_point_reading)
                                            ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                                            : "bg-rose-500/10 border-rose-500/30 text-rose-400"
                                    )} data-testid="calibration-auto-result">
                                        {isIcePointOk(form.ice_point_reading) && isBoilingPointOk(form.boiling_point_reading) ? (
                                            <><CheckCircle className="h-4 w-4" /><span className="text-sm font-medium">PASS — Both readings within tolerance</span></>
                                        ) : (
                                            <><AlertTriangle className="h-4 w-4" /><span className="text-sm font-medium">FAIL — One or more readings outside tolerance. Corrective action required.</span></>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div>
                                <Label className="text-slate-300">Next Due Date</Label>
                                <Input type="date" value={form.next_due_date} onChange={(e) => setForm({ ...form, next_due_date: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="calibration-next-due" />
                            </div>
                            {form.ice_point_reading !== '' && form.boiling_point_reading !== '' &&
                             !(isIcePointOk(form.ice_point_reading) && isBoilingPointOk(form.boiling_point_reading)) && (
                                <div className="sm:col-span-2">
                                    <Label className="text-slate-300">Corrective Action *</Label>
                                    <Input value={form.corrective_action} onChange={(e) => setForm({ ...form, corrective_action: e.target.value })}
                                        className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="What action was taken?" />
                                </div>
                            )}
                            <div className="sm:col-span-2">
                                <Label className="text-slate-300">Notes</Label>
                                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Additional notes" />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                            <Button onClick={handleCreate} className="brand-btn font-medium" data-testid="save-calibration-button">Save Log</Button>
                            <Button variant="outline" onClick={() => setShowForm(false)} className="border-slate-700 text-slate-300">Cancel</Button>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-slate-900/60 rounded-xl animate-pulse" />)}</div>
                ) : logs.length === 0 ? (
                    <div className="text-center py-16 text-slate-500">
                        <Gauge className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>No calibration logs yet</p>
                        <p className="text-xs mt-1">SFBB requires regular probe thermometer calibration checks</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {logs.map((log) => (
                            <div key={log.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid={`calibration-log-${log.id}`}>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                    <div className={`p-2 rounded-lg shrink-0 ${log.passed ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>
                                        {log.passed ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="text-sm font-semibold text-slate-200">{log.probe_name}</h3>
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${log.passed ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                                                {log.passed ? 'PASS' : 'FAIL'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                                            <span>Ice: {log.ice_point_reading}°C</span>
                                            <span>Boiling: {log.boiling_point_reading}°C</span>
                                            <span>{formatDate(log.calibration_date)}</span>
                                            <span>By: {log.recorded_by_name}</span>
                                        </div>
                                        {log.corrective_action && (
                                            <p className="text-xs text-amber-400 mt-1">Action: {log.corrective_action}</p>
                                        )}
                                        {log.next_due_date && (
                                            <p className="text-xs text-slate-500 mt-0.5">Next due: {formatDate(log.next_due_date)}</p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {log.signed_off_by ? (
                                            <span className="text-xs text-emerald-400 flex items-center gap-1">
                                                <FileCheck className="h-3.5 w-3.5" /> Signed: {log.signed_off_by}
                                            </span>
                                        ) : isManager ? (
                                            <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 text-xs h-7"
                                                onClick={() => handleSignOff(log.id)} data-testid={`sign-off-calibration-${log.id}`}>
                                                <FileCheck className="h-3 w-3 mr-1" /> Sign Off
                                            </Button>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
}
